import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Heart, X, Star, MapPin, Briefcase, Users, Filter, Search, UserCheck, Gift, Sparkles, Radar } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import ProximityToggle from '../components/ProximityToggle';
import ProximityEnableModal from '../components/ProximityEnableModal';
import { useProximity } from '../context/ProximityContext';
import { getFirstName } from '../utils/nameUtils';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExplorePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSection = searchParams.get('section') || 'match';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [profiles, setProfiles] = useState([]);
  const [browseMembers, setBrowseMembers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [favoriteGuests, setFavoriteGuests] = useState([]);
  const [favoriteHosts, setFavoriteHosts] = useState([]);
  const [favoriteGuestIds, setFavoriteGuestIds] = useState([]);
  const [favoriteHostIds, setFavoriteHostIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [favoriteTab, setFavoriteTab] = useState('guests');
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState(null);
  const [filters, setFilters] = useState({
    gender: '',
    minAge: '',
    maxAge: '',
    membershipTier: '',
    incomeVerified: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Proximity state
  const [showProximityModal, setShowProximityModal] = useState(false);
  const { 
    proximityEnabled, 
    nearbyMembers, 
    nearbyCount, 
    loadingNearby,
    fetchNearbyMembers 
  } = useProximity();
  
  // Theme
  const { isDark, colors: themeColors } = useTheme();

  useEffect(() => {
    fetchSwipeProfiles();
    fetchBrowseMembers();
    fetchFavorites();
    fetchMatches();
  }, []);

  const fetchSwipeProfiles = async () => {
    try {
      const response = await axios.get(`${API}/matches/swipe`);
      setProfiles(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setLoading(false);
    }
  };

  const fetchBrowseMembers = async () => {
    try {
      const response = await axios.get(`${API}/members/browse`);
      setBrowseMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const guestsRes = await axios.get(`${API}/favorites?user_id=current_user&type=guest`);
      const hostsRes = await axios.get(`${API}/favorites?user_id=current_user&type=host`);
      
      const guestsWithDetails = await Promise.all(
        guestsRes.data.map(async (fav) => {
          try {
            const userRes = await axios.get(`${API}/users/${fav.favorited_user_id}`);
            return { ...fav, user: userRes.data };
          } catch (error) {
            return null;
          }
        })
      );
      
      const hostsWithDetails = await Promise.all(
        hostsRes.data.map(async (fav) => {
          try {
            const userRes = await axios.get(`${API}/users/${fav.favorited_user_id}`);
            return { ...fav, user: userRes.data };
          } catch (error) {
            return null;
          }
        })
      );
      
      const validGuests = guestsWithDetails.filter(f => f && f.user);
      const validHosts = hostsWithDetails.filter(f => f && f.user);
      
      setFavoriteGuests(validGuests);
      setFavoriteHosts(validHosts);
      setFavoriteGuestIds(validGuests.map(f => f.favorited_user_id));
      setFavoriteHostIds(validHosts.map(f => f.favorited_user_id));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const handleSwipe = async (direction) => {
    setSwipeDirection(direction);
    
    const currentProfile = profiles[currentIndex];
    
    setTimeout(async () => {
      if (direction === 'right' && currentProfile) {
        // Always show match modal when liking someone (for demo/sales purposes)
        setMatchedProfile(currentProfile);
        setShowMatchModal(true);
        
        // Also track the interaction in backend
        try {
          await axios.post(`${API}/matches/interact`, {
            profile_id: currentProfile.id,
            action: 'like'
          });
          fetchMatches();
        } catch (error) {
          console.error('Error tracking interaction:', error);
        }
      }
      
      if (currentIndex < profiles.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
      setSwipeDirection(null);
    }, 300);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleSwipe('left');
    }
    if (isRightSwipe) {
      handleSwipe('right');
    }
  };

  const addToFavorites = async (profile, type) => {
    try {
      await axios.post(`${API}/favorites`, {
        user_id: 'current_user',
        favorited_user_id: profile.id,
        type: type
      });
      fetchFavorites();
    } catch (error) {
      console.error('Error adding to favorites:', error);
    }
  };

  const removeFavorite = async (favoriteId) => {
    try {
      await axios.delete(`${API}/favorites/${favoriteId}`);
      fetchFavorites();
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const applyFilters = async () => {
    try {
      let url = `${API}/members/browse?`;
      if (filters.gender) url += `gender=${filters.gender}&`;
      if (filters.minAge) url += `min_age=${filters.minAge}&`;
      if (filters.maxAge) url += `max_age=${filters.maxAge}&`;
      if (filters.membershipTier) url += `membership_tier=${filters.membershipTier}&`;
      if (filters.incomeVerified) url += `income_verified=${filters.incomeVerified}&`;
      
      const response = await axios.get(url);
      setBrowseMembers(response.data);
      setShowFilters(false);
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const renderIncomeBadge = (incomeLevel) => {
    if (!incomeLevel) return null;
    
    // Map legacy 'silver' to 'gold' for backwards compatibility
    const level = incomeLevel === 'silver' ? 'gold' : incomeLevel;
    
    const badges = {
      gold: (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 8px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #FFD700 0%, #B8860B 100%)',
          fontSize: '10px',
          fontWeight: 'bold',
          color: '#2C1B13'
        }}>
          💎 Gold
        </span>
      ),
      diamond: (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 8px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #87CEEB 0%, #4682B4 100%)',
          fontSize: '10px',
          fontWeight: 'bold',
          color: '#FFFFFF'
        }}>
          💎 Diamond
        </span>
      )
    };
    
    return badges[level] || badges['diamond'];
  };

  const currentProfile = profiles[currentIndex];

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ marginBottom: '12px' }}>
          <NearULogo size="small" showText={true} />
        </div>
        <h1 className="page-title" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>EXPLORE</h1>
        <p className="page-subtitle" style={{ letterSpacing: '1px' }}>Discover • Connect • Experience</p>
      </div>

      {/* Proximity Status Bar */}
      <div style={{ padding: '0 16px', marginBottom: '8px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: isDark ? 'rgba(26, 15, 10, 0.6)' : 'rgba(253, 252, 250, 0.95)',
          borderRadius: '10px',
          border: `1px solid ${isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.15)'}`
        }}>
          <ProximityToggle compact={true} />
          {proximityEnabled && nearbyCount > 0 && (
            <span style={{
              padding: '4px 10px',
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(56, 142, 60, 0.1) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              borderRadius: '12px',
              color: '#4CAF50',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {nearbyCount} nearby
            </span>
          )}
        </div>
      </div>

      {/* Section Navigation */}
      <div style={{ padding: '16px', paddingBottom: '0' }}>
        <div style={{
          background: isDark ? 'rgba(230, 215, 195, 0.05)' : 'rgba(139, 105, 20, 0.04)',
          border: `1px solid ${isDark ? 'rgba(165, 124, 51, 0.4)' : 'rgba(139, 105, 20, 0.2)'}`,
          borderRadius: '16px',
          padding: '6px',
          display: 'flex',
          gap: '6px',
          marginBottom: '20px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          {/* Nearby Now Tab */}
          <button
            onClick={() => {
              if (!proximityEnabled) {
                setShowProximityModal(true);
              } else {
                setActiveSection('nearby');
                fetchNearbyMembers();
              }
            }}
            data-testid="nearby-tab"
            style={{
              flex: '1 0 auto',
              padding: '10px 16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontFamily: 'Cormorant Garamond, serif',
              whiteSpace: 'nowrap',
              position: 'relative',
              ...(activeSection === 'nearby' ? {
                background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                color: '#FFF',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
              } : proximityEnabled ? {
                background: 'rgba(76, 175, 80, 0.1)',
                color: '#4CAF50',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              } : {
                background: 'transparent',
                color: '#8A7355'
              })
            }}
          >
            <Radar size={16} style={{ 
              display: 'inline', 
              marginRight: '6px', 
              verticalAlign: 'middle',
              animation: proximityEnabled && nearbyCount > 0 ? 'pulse 2s infinite' : 'none'
            }} />
            Nearby
            {nearbyCount > 0 && (
              <span style={{
                marginLeft: '6px',
                padding: '2px 6px',
                background: activeSection === 'nearby' ? 'rgba(255,255,255,0.3)' : 'rgba(76, 175, 80, 0.3)',
                borderRadius: '8px',
                fontSize: '11px'
              }}>
                {nearbyCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('match')}
            style={{
              flex: '1 0 auto',
              padding: '10px 16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontFamily: 'Cormorant Garamond, serif',
              whiteSpace: 'nowrap',
              ...(activeSection === 'match' ? {
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              } : {
                background: 'transparent',
                color: isDark ? '#E6D7C3' : '#4A4035'
              })
            }}
          >
            <Heart size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Match
          </button>
          <button
            onClick={() => setActiveSection('browse')}
            style={{
              flex: '1 0 auto',
              padding: '10px 16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontFamily: 'Cormorant Garamond, serif',
              whiteSpace: 'nowrap',
              ...(activeSection === 'browse' ? {
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              } : {
                background: 'transparent',
                color: isDark ? '#E6D7C3' : '#4A4035'
              })
            }}
          >
            <Search size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Browse
          </button>
          <button
            onClick={() => setActiveSection('matches')}
            style={{
              flex: '1 0 auto',
              padding: '10px 16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontFamily: 'Cormorant Garamond, serif',
              whiteSpace: 'nowrap',
              ...(activeSection === 'matches' ? {
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              } : {
                background: 'transparent',
                color: isDark ? '#E6D7C3' : '#4A4035'
              })
            }}
          >
            <UserCheck size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Matches
          </button>
          <button
            onClick={() => setActiveSection('favorites')}
            style={{
              flex: '1 0 auto',
              padding: '10px 16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontFamily: 'Cormorant Garamond, serif',
              whiteSpace: 'nowrap',
              ...(activeSection === 'favorites' ? {
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              } : {
                background: 'transparent',
                color: isDark ? '#E6D7C3' : '#4A4035'
              })
            }}
          >
            <Star size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Favorites
          </button>
        </div>
      </div>

      {/* Nearby Now Section */}
      {activeSection === 'nearby' && (
        <div style={{ padding: '16px', paddingBottom: '100px' }} data-testid="nearby-section">
          {!proximityEnabled ? (
            <div className="card" style={{ 
              padding: '40px 24px', 
              textAlign: 'center',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              <Radar size={64} style={{ color: 'rgba(138, 115, 85, 0.4)', margin: '0 auto 20px' }} />
              <h3 style={{
                fontSize: '20px',
                fontFamily: 'Cinzel, serif',
                color: '#D7B56D',
                marginBottom: '12px'
              }}>
                Proximity Discovery is Off
              </h3>
              <p style={{ 
                color: '#8A7355', 
                fontSize: '14px',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Turn on Proximity Discovery to see members within 60 feet who also have this feature enabled.
              </p>
              <button
                onClick={() => setShowProximityModal(true)}
                data-testid="turn-on-proximity-btn"
                style={{
                  padding: '14px 32px',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#FFF',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)'
                }}
              >
                Turn On
              </button>
            </div>
          ) : loadingNearby ? (
            <div style={{ textAlign: 'center', paddingTop: '60px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid rgba(76, 175, 80, 0.2)',
                borderTop: '4px solid #4CAF50',
                borderRadius: '50%',
                margin: '0 auto',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#4CAF50', marginTop: '16px', fontSize: '14px' }}>
                Scanning for nearby members...
              </p>
            </div>
          ) : nearbyMembers.length === 0 ? (
            <div className="card" style={{ 
              padding: '40px 24px', 
              textAlign: 'center',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              <Radar size={64} style={{ color: 'rgba(76, 175, 80, 0.3)', margin: '0 auto 20px' }} />
              <h3 style={{
                fontSize: '20px',
                fontFamily: 'Cinzel, serif',
                color: '#4CAF50',
                marginBottom: '12px'
              }}>
                No Members Nearby Right Now
              </h3>
              <p style={{ 
                color: '#8A7355', 
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                We'll show participating members here when they're within 60 feet.
              </p>
              <p style={{ 
                color: isDark ? '#A57C33' : '#8B6914', 
                fontSize: '12px',
                marginTop: '16px'
              }}>
                Your exact location is never shown to other members.
              </p>
            </div>
          ) : (
            <>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#4CAF50',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Radar size={18} style={{ animation: 'pulse 2s infinite' }} />
                  Nearby Now
                </h3>
                <span style={{
                  color: '#8A7355',
                  fontSize: '13px'
                }}>
                  {nearbyMembers.length} member{nearbyMembers.length !== 1 ? 's' : ''} within 60 ft
                </span>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                gap: '12px' 
              }}>
                {nearbyMembers.map((member) => (
                  <div
                    key={member.id}
                    className="card"
                    onClick={() => navigate(`/user/${member.id}`)}
                    data-testid={`nearby-member-${member.id}`}
                    style={{
                      cursor: 'pointer',
                      overflow: 'hidden',
                      padding: 0,
                      transition: 'all 0.3s',
                      border: '1px solid rgba(76, 175, 80, 0.3)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <div style={{ position: 'relative', aspectRatio: '3/4' }}>
                      <img
                        src={member.photo || member.photos?.[0]}
                        alt={member.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Nearby Indicator */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: 'rgba(76, 175, 80, 0.9)',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        color: '#FFF'
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#FFF',
                          animation: 'pulse 1.5s infinite'
                        }} />
                        {member.nearby_indicator || 'Within 60 ft'}
                      </div>
                    </div>
                    <div style={{ padding: '12px' }}>
                      <h3 style={{
                        color: '#D4AF37',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        fontFamily: 'Cormorant Garamond, serif',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {getFirstName(member.name)}{member.age ? `, ${member.age}` : ''}
                      </h3>
                      {member.elite && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                          borderRadius: '10px',
                          fontSize: '9px',
                          color: '#2C1B13',
                          fontWeight: 'bold',
                          marginBottom: '6px'
                        }}>
                          ELITE
                        </span>
                      )}
                      {member.occupation && (
                        <p style={{
                          color: isDark ? '#A57C33' : '#8B6914',
                          fontSize: '12px',
                          marginTop: '4px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {member.occupation}
                        </p>
                      )}
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/chat/${member.id}`, { state: { user: member } });
                          }}
                          className="btn-primary"
                          style={{ 
                            flex: 1, 
                            padding: '8px', 
                            fontSize: '11px', 
                            borderRadius: '6px' 
                          }}
                        >
                          Message
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/send-gift/${member.id}`, { state: { recipient: member } });
                          }}
                          style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                            borderRadius: '6px',
                            background: 'transparent',
                            border: '1px solid #D7B56D',
                            color: '#D7B56D',
                            cursor: 'pointer'
                          }}
                        >
                          <Gift size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                          Gift
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Match Section */}
      {activeSection === 'match' && (
        <div style={{ padding: '16px', paddingBottom: '100px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', paddingTop: '60px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid rgba(212, 175, 55, 0.2)',
                borderTop: '4px solid #D4AF37',
                borderRadius: '50%',
                margin: '0 auto',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          ) : currentProfile ? (
            <div 
              className="card" 
              style={{ 
                maxWidth: '500px', 
                margin: '0 auto',
                borderColor: 'rgba(165, 124, 51, 0.6)',
                overflow: 'hidden',
                transform: swipeDirection === 'left' ? 'translateX(-100px) rotate(-10deg)' : swipeDirection === 'right' ? 'translateX(100px) rotate(10deg)' : 'none',
                opacity: swipeDirection ? 0 : 1,
                transition: 'all 0.3s ease-out',
                touchAction: 'pan-y'
              }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div style={{ position: 'relative', aspectRatio: '3/4' }}>
                <img
                  src={currentProfile.photos?.[0] || currentProfile.photo}
                  alt={currentProfile.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {/* Gradient overlay for better text visibility */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '120px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  pointerEvents: 'none'
                }} />
                {/* View Profile Button - Subtle and elegant */}
                <button
                  onClick={() => navigate(`/user/${currentProfile.id}`)}
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(10px)',
                    padding: '10px 24px',
                    borderRadius: '20px',
                    color: isDark ? '#E6D7C3' : '#4A4035',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid rgba(215, 181, 109, 0.4)',
                    cursor: 'pointer',
                    zIndex: 10,
                    transition: 'all 0.3s ease'
                  }}
                >
                  View Profile
                </button>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <h2 style={{ 
                    fontSize: '32px', 
                    fontFamily: 'Cinzel, Playfair Display, serif',
                    background: 'linear-gradient(135deg, #D7B56D 0%, #E6D7C3 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    {getFirstName(currentProfile.name)}
                  </h2>
                  {currentProfile.age && (
                    <span style={{ fontSize: '28px', color: isDark ? '#E6D7C3' : '#4A4035' }}>{currentProfile.age}</span>
                  )}
                </div>
                
                {currentProfile.elite && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: '#2C1B13',
                    fontWeight: 'bold',
                    marginBottom: '16px'
                  }}>
                    <Star size={14} style={{ marginRight: '6px' }} fill="currentColor" />
                    ELITE MEMBER
                  </span>
                )}
                
                {currentProfile.occupation && (
                  <p style={{ 
                    color: isDark ? '#E6D7C3' : '#4A4035', 
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <Briefcase size={16} style={{ color: '#D4AF37' }} />
                    {currentProfile.occupation}
                  </p>
                )}
                
                {currentProfile.location && (
                  <p style={{ 
                    color: isDark ? '#A57C33' : '#8B6914', 
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <MapPin size={16} />
                    {currentProfile.location}
                  </p>
                )}
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                padding: '0 24px 24px 24px' 
              }}>
                <button
                  onClick={() => handleSwipe('left')}
                  className="btn-secondary"
                  style={{
                    flex: 1,
                    padding: '16px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <X size={24} />
                  Pass
                </button>
                <button
                  onClick={() => handleSwipe('right')}
                  className="btn-primary"
                  style={{
                    flex: 1,
                    padding: '16px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Heart size={24} fill="currentColor" />
                  Like
                </button>
              </div>
            </div>
          ) : (
            <div className="card" style={{ 
              maxWidth: '500px', 
              margin: '0 auto',
              padding: '60px 24px',
              textAlign: 'center'
            }}>
              <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '18px' }}>No more profiles to show</p>
            </div>
          )}
        </div>
      )}

      {/* Browse Members Section */}
      {activeSection === 'browse' && (
        <div style={{ padding: '16px', paddingBottom: '100px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '14px' }}>{browseMembers.length} members</p>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px', borderRadius: '8px' }}
            >
              <Filter size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ color: '#D4AF37', fontSize: '18px', marginBottom: '16px', fontFamily: 'Cinzel, serif' }}>Filter Members</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#D4AF37', marginBottom: '6px', fontSize: '13px' }}>Gender</label>
                  <select
                    value={filters.gender}
                    onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                    className="input-field"
                    style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                  >
                    <option value="">All</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#D4AF37', marginBottom: '6px', fontSize: '13px' }}>Min Age</label>
                  <input
                    type="number"
                    value={filters.minAge}
                    onChange={(e) => setFilters({ ...filters, minAge: e.target.value })}
                    className="input-field"
                    placeholder="21"
                    style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#D4AF37', marginBottom: '6px', fontSize: '13px' }}>Max Age</label>
                  <input
                    type="number"
                    value={filters.maxAge}
                    onChange={(e) => setFilters({ ...filters, maxAge: e.target.value })}
                    className="input-field"
                    placeholder="99"
                    style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#D4AF37', marginBottom: '6px', fontSize: '13px' }}>Membership</label>
                  <select
                    value={filters.membershipTier}
                    onChange={(e) => setFilters({ ...filters, membershipTier: e.target.value })}
                    className="input-field"
                    style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                  >
                    <option value="">All</option>
                    <option value="elite">Elite</option>
                    <option value="basic">Basic</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={applyFilters} className="btn-primary" style={{ flex: 1, padding: '10px', fontSize: '14px' }}>
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    setFilters({ gender: '', minAge: '', maxAge: '', membershipTier: '', incomeVerified: '' });
                    fetchBrowseMembers();
                  }}
                  className="btn-secondary"
                  style={{ padding: '10px 20px', fontSize: '14px' }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
            gap: '12px' 
          }}>
            {browseMembers.map((member) => (
              <div
                key={member.id}
                className="card"
                onClick={() => navigate(`/user/${member.id}`)}
                style={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  padding: 0,
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{ position: 'relative', aspectRatio: '3/4' }}>
                  <img
                    src={member.photo}
                    alt={member.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {member.online && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '12px',
                      height: '12px',
                      background: '#28a745',
                      borderRadius: '50%',
                      border: '2px solid white',
                      boxShadow: '0 0 8px rgba(40, 167, 69, 0.6)'
                    }}></div>
                  )}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    display: 'flex',
                    gap: '4px'
                  }}>
                    {favoriteGuestIds.includes(member.id) && (
                      <span style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                      }} title="Favorite Guest">
                        👥
                      </span>
                    )}
                    {favoriteHostIds.includes(member.id) && (
                      <span style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                      }} title="Favorite Host">
                        ⭐
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '12px' }}>
                  <h3 style={{
                    color: '#D4AF37',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                    fontFamily: 'Cormorant Garamond, serif',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {getFirstName(member.name)}, {member.age}
                  </h3>
                  {member.elite && (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                      borderRadius: '10px',
                      fontSize: '9px',
                      color: '#2C1B13',
                      fontWeight: 'bold',
                      marginBottom: '6px'
                    }}>
                      ELITE
                    </span>
                  )}
                  {renderIncomeBadge(member.income_verified)}
                  <p style={{
                    color: isDark ? '#A57C33' : '#8B6914',
                    fontSize: '12px',
                    marginTop: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {member.occupation}
                  </p>
                  {/* Send Gift Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/send-gift/${member.id}`, { state: { recipient: member } });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '11px',
                      borderRadius: '6px',
                      marginTop: '10px',
                      background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.15) 100%)',
                      border: '1px solid #D7B56D',
                      color: '#D7B56D',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <Gift size={12} />
                    Send Gift
                  </button>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToFavorites(member, 'guest');
                      }}
                      disabled={favoriteGuestIds.includes(member.id)}
                      className="btn-secondary"
                      style={{
                        flex: 1,
                        padding: '6px',
                        fontSize: '10px',
                        borderRadius: '6px',
                        opacity: favoriteGuestIds.includes(member.id) ? 0.5 : 1,
                        cursor: favoriteGuestIds.includes(member.id) ? 'default' : 'pointer'
                      }}
                    >
                      {favoriteGuestIds.includes(member.id) ? '✓ Guest' : '+ Guest'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToFavorites(member, 'host');
                      }}
                      disabled={favoriteHostIds.includes(member.id)}
                      className="btn-secondary"
                      style={{
                        flex: 1,
                        padding: '6px',
                        fontSize: '10px',
                        borderRadius: '6px',
                        opacity: favoriteHostIds.includes(member.id) ? 0.5 : 1,
                        cursor: favoriteHostIds.includes(member.id) ? 'default' : 'pointer'
                      }}
                    >
                      {favoriteHostIds.includes(member.id) ? '✓ Host' : '+ Host'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Matches Section */}
      {activeSection === 'matches' && (
        <div style={{ padding: '16px', paddingBottom: '100px' }}>
          <p style={{ color: isDark ? '#A57C33' : '#8B6914', marginBottom: '16px', fontSize: '14px' }}>
            {matches.length} matches
          </p>
          {matches.length === 0 ? (
            <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
              <Heart size={64} style={{ color: 'rgba(230, 215, 195, 0.3)', margin: '0 auto 16px' }} />
              <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '16px' }}>No matches yet. Keep swiping!</p>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '16px' 
            }}>
              {matches.map((match) => (
                <div 
                  key={match.id} 
                  className="card" 
                  style={{ 
                    overflow: 'hidden', 
                    padding: 0, 
                    cursor: 'pointer',
                    borderRadius: '16px',
                    border: '1px solid rgba(165, 124, 51, 0.3)'
                  }}
                  onClick={() => navigate(`/user/${match.matched_user_id}`)}
                >
                  <div style={{ position: 'relative', aspectRatio: '1' }}>
                    <img
                      src={match.photo}
                      alt={match.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {/* Online indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: '#4CAF50',
                      border: '2px solid #1A0F0A'
                    }} />
                  </div>
                  <div style={{ padding: '14px 12px' }}>
                    <h3 style={{
                      color: '#D4AF37',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '2px',
                      fontFamily: 'Cormorant Garamond, serif',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {getFirstName(match.name)}
                    </h3>
                    <p style={{ color: '#8A7355', fontSize: '10px', marginBottom: '12px' }}>
                      Matched {new Date(match.matched_at).toLocaleDateString()}
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/messages');
                        }}
                        style={{ 
                          flex: 1, 
                          padding: '10px 8px', 
                          fontSize: '12px',
                          fontWeight: '500',
                          borderRadius: '10px',
                          background: '#D7B56D',
                          border: 'none',
                          color: '#2C1B13',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Message
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/send-gift/${match.matched_user_id}`, { state: { recipient: match } });
                        }}
                        style={{ 
                          flex: 1, 
                          padding: '10px 8px', 
                          fontSize: '12px',
                          fontWeight: '500',
                          borderRadius: '10px',
                          background: 'transparent',
                          border: '1.5px solid #D7B56D',
                          color: '#D7B56D',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Gift
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Favorites Section with Tabs */}
      {activeSection === 'favorites' && (
        <div style={{ padding: '16px', paddingBottom: '100px' }}>
          {/* Favorite Tabs */}
          <div style={{
            background: 'rgba(230, 215, 195, 0.05)',
            border: '1px solid rgba(165, 124, 51, 0.4)',
            borderRadius: '12px',
            padding: '4px',
            display: 'flex',
            gap: '4px',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => setFavoriteTab('guests')}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontFamily: 'Cormorant Garamond, serif',
                ...(favoriteTab === 'guests' ? {
                  background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                  color: '#2C1B13',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                } : {
                  background: 'transparent',
                  color: isDark ? '#E6D7C3' : '#4A4035'
                })
              }}
            >
              <Users size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              Favorite Guests ({favoriteGuests.length})
            </button>
            <button
              onClick={() => setFavoriteTab('hosts')}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontFamily: 'Cormorant Garamond, serif',
                ...(favoriteTab === 'hosts' ? {
                  background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                  color: '#2C1B13',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                } : {
                  background: 'transparent',
                  color: isDark ? '#E6D7C3' : '#4A4035'
                })
              }}
            >
              <Star size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              Favorite Hosts ({favoriteHosts.length})
            </button>
          </div>

          {/* Favorite Guests Content */}
          {favoriteTab === 'guests' && (
            favoriteGuests.length === 0 ? (
              <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
                <Users size={64} style={{ color: 'rgba(230, 215, 195, 0.3)', margin: '0 auto 16px' }} />
                <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '16px' }}>No favorite guests yet</p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                gap: '12px' 
              }}>
                {favoriteGuests.map((fav) => (
                  <div key={fav.id} className="card" style={{ overflow: 'hidden', padding: 0 }}>
                    <div
                      style={{ cursor: 'pointer', position: 'relative', aspectRatio: '3/4' }}
                      onClick={() => navigate(`/user/${fav.user.id}`)}
                    >
                      <img
                        src={fav.user.photos?.[0] || fav.user.photo}
                        alt={fav.user.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ padding: '12px' }}>
                      <h3 style={{
                        color: '#D4AF37',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        fontFamily: 'Cormorant Garamond, serif'
                      }}>
                        {getFirstName(fav.user.name)}
                      </h3>
                      <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '12px', marginBottom: '10px' }}>
                        {fav.user.occupation}
                      </p>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => navigate(`/send-gift/${fav.user.id}`, { state: { recipient: fav.user } })}
                          className="btn-primary"
                          style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px' }}
                        >
                          Send Gift
                        </button>
                        <button
                          onClick={() => removeFavorite(fav.id)}
                          style={{
                            padding: '8px 12px',
                            fontSize: '11px',
                            borderRadius: '8px',
                            background: 'rgba(165, 124, 51, 0.1)',
                            color: '#8A7355',
                            border: '1px solid rgba(165, 124, 51, 0.3)',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Favorite Hosts Content */}
          {favoriteTab === 'hosts' && (
            favoriteHosts.length === 0 ? (
              <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
                <Star size={64} style={{ color: 'rgba(230, 215, 195, 0.3)', margin: '0 auto 16px' }} />
                <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '16px' }}>No favorite hosts yet</p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                gap: '12px' 
              }}>
                {favoriteHosts.map((fav) => (
                  <div key={fav.id} className="card" style={{ overflow: 'hidden', padding: 0 }}>
                    <div
                      style={{ cursor: 'pointer', position: 'relative', aspectRatio: '3/4' }}
                      onClick={() => navigate(`/user/${fav.user.id}`)}
                    >
                      <img
                        src={fav.user.photos?.[0] || fav.user.photo}
                        alt={fav.user.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ padding: '12px' }}>
                      <h3 style={{
                        color: '#D4AF37',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        fontFamily: 'Cormorant Garamond, serif'
                      }}>
                        {getFirstName(fav.user.name)}
                      </h3>
                      <p style={{ color: isDark ? '#A57C33' : '#8B6914', fontSize: '12px', marginBottom: '10px' }}>
                        {fav.user.occupation}
                      </p>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => navigate(`/send-gift/${fav.user.id}`, { state: { recipient: fav.user } })}
                          className="btn-primary"
                          style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px' }}
                        >
                          Send Gift
                        </button>
                        <button
                          onClick={() => removeFavorite(fav.id)}
                          style={{
                            padding: '8px 12px',
                            fontSize: '11px',
                            borderRadius: '8px',
                            background: 'rgba(165, 124, 51, 0.1)',
                            color: '#8A7355',
                            border: '1px solid rgba(165, 124, 51, 0.3)',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* Match Celebration Modal */}
      {showMatchModal && matchedProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.3s ease'
        }}>
          {/* Sparkle decorations */}
          <div style={{ position: 'absolute', top: '10%', left: '15%' }}>
            <Sparkles size={32} color="#D7B56D" style={{ animation: 'pulse 1.5s infinite' }} />
          </div>
          <div style={{ position: 'absolute', top: '15%', right: '20%' }}>
            <Sparkles size={24} color="#A57C33" style={{ animation: 'pulse 1.5s infinite 0.5s' }} />
          </div>
          <div style={{ position: 'absolute', bottom: '20%', left: '10%' }}>
            <Sparkles size={28} color="#D7B56D" style={{ animation: 'pulse 1.5s infinite 0.3s' }} />
          </div>
          <div style={{ position: 'absolute', bottom: '25%', right: '15%' }}>
            <Sparkles size={20} color="#A57C33" style={{ animation: 'pulse 1.5s infinite 0.7s' }} />
          </div>

          {/* It's a Match text */}
          <h1 style={{
            fontSize: '36px',
            fontFamily: 'Cinzel, serif',
            background: 'linear-gradient(135deg, #D7B56D 0%, #F5E6C8 50%, #A57C33 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            textAlign: 'center'
          }}>
            It's a Match!
          </h1>
          <p style={{
            color: isDark ? '#A57C33' : '#8B6914',
            fontSize: '16px',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            You and {getFirstName(matchedProfile.name)} liked each other
          </p>

          {/* Profile photos */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '40px',
            position: 'relative'
          }}>
            {/* Your photo */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #D7B56D',
              boxShadow: '0 0 30px rgba(215, 181, 109, 0.4)',
              zIndex: 1
            }}>
              <img
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200"
                alt="You"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            
            {/* Heart in the middle */}
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 -15px',
              zIndex: 2,
              boxShadow: '0 0 20px rgba(215, 181, 109, 0.6)'
            }}>
              <Heart size={24} fill="#2C1B13" color="#2C1B13" />
            </div>
            
            {/* Match's photo */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #D7B56D',
              boxShadow: '0 0 30px rgba(215, 181, 109, 0.4)',
              zIndex: 1
            }}>
              <img
                src={matchedProfile.photos?.[0] || matchedProfile.photo}
                alt={matchedProfile.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          </div>

          {/* Gift suggestion */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.1) 100%)',
            border: '1px solid rgba(215, 181, 109, 0.4)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            textAlign: 'center',
            maxWidth: '320px',
            width: '100%'
          }}>
            <Gift size={28} color="#D7B56D" style={{ marginBottom: '12px' }} />
            <h3 style={{
              color: isDark ? '#E6D7C3' : '#4A4035',
              fontSize: '18px',
              fontFamily: 'Cinzel, serif',
              marginBottom: '8px'
            }}>
              Make a Great First Impression
            </h3>
            <p style={{
              color: '#8A7355',
              fontSize: '14px',
              marginBottom: '16px',
              lineHeight: '1.5'
            }}>
              Stand out from the crowd! Send {getFirstName(matchedProfile.name)} a gift to start your connection.
            </p>
            <button
              onClick={() => {
                setShowMatchModal(false);
                navigate(`/send-gift/${matchedProfile.id}`, { state: { recipient: matchedProfile } });
              }}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#2C1B13',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)'
              }}
            >
              <Gift size={20} />
              Send a Gift
            </button>
          </div>

          {/* Secondary actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            width: '100%',
            maxWidth: '320px'
          }}>
            <button
              onClick={() => {
                setShowMatchModal(false);
                navigate(`/chat/${matchedProfile.id}`, { state: { user: matchedProfile } });
              }}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(215, 181, 109, 0.1)',
                border: '1px solid #A57C33',
                borderRadius: '12px',
                color: '#D7B56D',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Send Message
            </button>
            <button
              onClick={() => setShowMatchModal(false)}
              style={{
                flex: 1,
                padding: '14px',
                background: 'transparent',
                border: '1px solid rgba(165, 124, 51, 0.4)',
                borderRadius: '12px',
                color: '#8A7355',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Keep Browsing
            </button>
          </div>
        </div>
      )}

      {/* Proximity Enable Modal */}
      <ProximityEnableModal
        isOpen={showProximityModal}
        onClose={() => setShowProximityModal(false)}
        onSuccess={() => {
          setActiveSection('nearby');
          fetchNearbyMembers();
        }}
      />
    </div>
  );
};

export default ExplorePage;
